// ============================================================================
// What a Quote Run row shows of the Quote Brain on a job the run handed over
// (batch-queue.js completionOutcome 'quote_brain_handoff'). The run writes the
// summary and hands the job on; the Quote Brain then writes the quote on the
// server. Without this the row said "Handed to the Quote Brain" for ever, and
// the operator had to open each job to see whether its quote was written.
//
// Read from the runs list (GET /api/brain/runs?projectId=, listQuoteBrainRuns),
// which has no side effects. The job's own banner (QuoteBrainBar) follows the
// run in detail. Keyed by project id; the Quote Run's poller
// (use-quote-brain-handoff-follow.ts) writes it and every Quote Run surface
// reads it through quoteRunRowWords, so they agree.
// ============================================================================

import type { QuoteBrainAutoStart, QuoteBrainRun } from '../services/quote-brain-runs';

export type HandoffFollow =
    /** No run yet; the server starts one by itself ('expected', 'waiting', 'retrying'). */
    | { state: 'starting'; retrying: boolean; reason: string | null }
    /** A run is queued, preparing or writing. */
    | { state: 'writing' }
    /** The run finished and the quote is on the job. */
    | { state: 'written' }
    /** The run's quote waits for the builder to compare (it held instead of writing). */
    | { state: 'held' }
    /** The run paused and waits for Continue on the job. */
    | { state: 'paused'; reason: string | null }
    /** The run could not finish, or was stopped. */
    | { state: 'stopped'; failed: boolean; reason: string | null }
    /** No run, and the server will not start one by itself ('gave_up' or 'no'). */
    | { state: 'not-started'; reason: string | null };

// The run's own stop codes ("S3: ...", "cap: ...") are the report page's; the
// builder reads the reason after them, as the banner prints it.
const cleanReason = (text: string | null | undefined): string | null => {
    const plain = String(text || '').replace(/^(S[1-6]|cap|gate):\s*/, '').trim();
    return plain || null;
};

/** The row's state from one runs-list read: the latest run that can touch the job (shadow runs never do). */
export function followFromRuns(
    { runs, autoStart }: { runs?: QuoteBrainRun[] | null; autoStart?: QuoteBrainAutoStart | null },
): HandoffFollow {
    const run = (Array.isArray(runs) ? runs : []).find((candidate) => candidate?.gateMode === 'on');
    if (run && !run.releasedAt) {
        switch (run.status) {
            case 'queued':
            case 'preparing':
            case 'running':
                return { state: 'writing' };
            case 'complete':
                return { state: 'written' };
            case 'held':
                return { state: 'held' };
            case 'paused':
                return { state: 'paused', reason: cleanReason(run.stopReason) };
            case 'failed':
                return { state: 'stopped', failed: true, reason: cleanReason(run.stopReason) };
            case 'cancelled':
                return { state: 'stopped', failed: false, reason: cleanReason(run.stopReason) };
            default:
                break;
        }
    }
    const start = autoStart?.state;
    if (start === 'expected' || start === 'waiting' || start === 'retrying') {
        return { state: 'starting', retrying: start === 'retrying', reason: cleanReason(autoStart?.reason) };
    }
    // 'started' with no run on the list is a run another read will show.
    if (start === 'started') return { state: 'writing' };
    return { state: 'not-started', reason: cleanReason(autoStart?.reason) };
}

/** True once nothing will change without the builder: the poller stops reading it. */
export function handoffFollowSettled(follow: HandoffFollow | null): boolean {
    if (!follow) return false;
    return follow.state !== 'starting' && follow.state !== 'writing';
}

const follows = new Map<string, HandoffFollow>();
const listeners = new Set<() => void>();
let version = 0;

function changed(): void {
    version += 1;
    listeners.forEach((fn) => fn());
}

const key = (projectId: unknown): string => (projectId == null ? '' : String(projectId));

export function noteHandoffFollow(projectId: unknown, follow: HandoffFollow | null): void {
    const id = key(projectId);
    if (!id) return;
    const before = follows.get(id) || null;
    if (JSON.stringify(before) === JSON.stringify(follow)) return;
    if (follow) follows.set(id, follow); else follows.delete(id);
    changed();
}

/** The last read of the Quote Brain on this handed-off job, or null before the first read. */
export function handoffFollowFor(projectId: unknown): HandoffFollow | null {
    return follows.get(key(projectId)) || null;
}

/** For React (useSyncExternalStore): changes whenever a read changes a row. */
export function subscribeHandoffFollows(fn: () => void): () => void {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
}

export function getHandoffFollowsVersion(): number {
    return version;
}

/** For tests. */
export function __resetHandoffFollows(): void {
    follows.clear();
    changed();
}
